/**
 * Phase 129 (Plan 129-05) — frontend client for the project Engineering tab.
 *
 * Endpoints (all flag-gated and team-gated server-side; the browser guard is presentation only):
 *   GET    /api/projects/:projectId/engineering/access
 *   GET    /api/projects/:projectId/engineering/github-repositories/available
 *   POST   /api/projects/:projectId/engineering/github-repositories       body { githubRepositoryId }
 *   DELETE /api/projects/:projectId/engineering/github-repositories/:githubRepositoryId
 *   GET    /api/projects/:projectId/engineering/robot
 *   PUT    /api/projects/:projectId/engineering/robot/queue-config
 *   POST   /api/projects/:projectId/engineering/robot/repository-provisioning   body { repository }
 *   POST   the onboarding draft route under .../engineering/robot   body { repository }
 *
 * Phase 130 (Plan 130-15): the project-scoped policy PUT and DELETE are gone; the robot read carries
 * each repository's policy (its factory.yml at main) read-only.
 */
import { apiDelete, apiGet, apiPost, apiPut } from './client';
import type {
  PaperclipCatalog,
  ProjectRepository,
  RepositoryProvisioningEntry,
  TriagerMode,
} from './botAdmin';
import type { FactoryRepoPolicyItem } from './factoryRepoPolicies';
import type { GithubRepository } from './projectRepositories';

export interface ProjectEngineeringAccess {
  projectId: number;
  member: true;
}

const base = (projectId: number): string => `/projects/${projectId}/engineering`;

/** 200 for any project team member; 403 for everyone else; 404 while the release flag is off. */
export async function fetchProjectEngineeringAccess(
  projectId: number,
): Promise<ProjectEngineeringAccess> {
  const res = await apiGet<{ data: ProjectEngineeringAccess }>(`${base(projectId)}/access`);
  return res.data;
}

export async function fetchProjectGithubAvailable(projectId: number): Promise<GithubRepository[]> {
  const res = await apiGet<{ data: GithubRepository[] }>(
    `${base(projectId)}/github-repositories/available`,
  );
  return res.data;
}

export async function linkProjectGithubRepository(
  projectId: number,
  githubRepositoryId: string,
): Promise<void> {
  await apiPost<{ data: { success: boolean } }>(`${base(projectId)}/github-repositories`, {
    githubRepositoryId,
  });
}

export async function unlinkProjectGithubRepository(
  projectId: number,
  githubRepositoryId: string,
): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(
    `${base(projectId)}/github-repositories/${githubRepositoryId}`,
  );
}

/**
 * Phase 129 (Plan 129-06, D-08, D-10): one project's Robot settings as the project team sees them.
 * Bot Admin's queue config minus the project name / slug, `greedyMode` and every `sre*` field, and
 * with the triager WIP reduced to counts: no holders (task codes and names), no bucket label or key
 * (a person's name).
 */
export interface ProjectEngineeringQueueConfig {
  id: number;
  projectId: number;
  enabled: boolean;
  sourceTaskStateIds: number[];
  sourceTaskStates: { id: number; name: string }[];
  pollIntervalSeconds: number;
  dailyBudget: number;
  autoMergeOnGreen: boolean;
  fallbackExecutor: string;
  triagerMode: TriagerMode;
  triagerWipLimit: number;
  triagerApprovalCap: number;
  triagerApprovals: { open: number; cap: number };
  triagerWip: {
    count: number;
    limit: number;
    buckets: { shared: boolean; count: number; full: boolean }[];
  };
  paperclipCompanyId: string | null;
  paperclipProjectId: string | null;
  repositories: ProjectRepository[];
  repositoryProvisioning: RepositoryProvisioningEntry[];
  dailyBudgetUsedCount: number;
  dailyBudgetResetDate: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One repository policy on the project's own list, with how many OTHER projects share it. */
export type ProjectEngineeringPolicyItem = FactoryRepoPolicyItem & { otherProjects: number };

export interface ProjectRobotView {
  projectId: number;
  /** null when the project has no Robot config yet. */
  config: ProjectEngineeringQueueConfig | null;
  taskStates: { id: number; name: string }[];
  catalog: PaperclipCatalog;
  policies: ProjectEngineeringPolicyItem[];
}

/**
 * PUT body of the member save. These are the only keys the server accepts (any other is a 400):
 * never `sre*`, `fallbackExecutor`, `autoMergeOnGreen` or `greedyMode`. `repositories` and
 * `paperclipProjectId` are sent only after the repository rows were edited (omitted = unchanged).
 */
export interface ProjectEngineeringQueueConfigInput {
  enabled: boolean;
  sourceTaskStateIds: number[];
  pollIntervalSeconds: number;
  dailyBudget: number;
  triagerMode?: TriagerMode;
  triagerWipLimit?: number;
  triagerApprovalCap?: number;
  paperclipCompanyId?: string | null;
  paperclipProjectId?: string | null;
  repositories?: ProjectRepository[];
}

export async function fetchProjectRobotView(projectId: number): Promise<ProjectRobotView> {
  const res = await apiGet<{ data: ProjectRobotView }>(`${base(projectId)}/robot`);
  return res.data;
}

export async function saveProjectQueueConfig(
  projectId: number,
  input: ProjectEngineeringQueueConfigInput,
): Promise<ProjectEngineeringQueueConfig> {
  const res = await apiPut<{ data: ProjectEngineeringQueueConfig }>(
    `${base(projectId)}/robot/queue-config`,
    input,
  );
  return res.data;
}

/** Retry the Factory project provisioning of one linked repository. */
export async function retryProjectRepositoryProvisioning(
  projectId: number,
  repository: string,
): Promise<RepositoryProvisioningEntry> {
  const res = await apiPost<{ data: RepositoryProvisioningEntry }>(
    `${base(projectId)}/robot/repository-provisioning`,
    { repository },
  );
  return res.data;
}

/** One open question of the onboarding draft: something the generator could not decide alone. */
export interface OnboardingQuestion {
  id: string;
  field: string;
  question: string;
  kind: 'choose_many' | 'choose_one' | 'confirm' | 'text';
  candidates: string[];
  default: string[];
  reason: string | null;
}

/** The generated first draft of a repository's factory files. Nothing of it is saved server-side. */
export interface OnboardingDraft {
  repository: string;
  mainSha: string;
  ciConfigPath: string | null;
  ciWorkflow: string | null;
  factoryYml: string;
  dockerfile: string | null;
  dockerignore: string | null;
  runnerBaseTag: string | null;
  questions: OnboardingQuestion[];
  notes: string[];
}

/**
 * Generate the factory onboarding draft of one repository (read-only: the server reads the
 * repository at main and returns text; it writes nothing and opens no pull request).
 */
export async function generateOnboardingDraft(
  projectId: number,
  repository: string,
): Promise<OnboardingDraft> {
  const res = await apiPost<{ data: OnboardingDraft }>(
    `${base(projectId)}/robot/onboarding-draft`,
    { repository },
  );
  return res.data;
}
